import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { useUserContext } from '@/lib/auth/useUserContext';
import { useViewScope } from '@/lib/scope/ViewScope';
import { uploadFile } from '@/lib/uploads';

type Section = 'open' | 'daily' | 'closing' | 'weekly';
type Status = 'open' | 'completed' | 'skipped';
type Cadence = 'daily' | 'weekly' | 'once';

interface Requirements {
  photoRequired: boolean;
  signatureRequired: boolean;
  numberRequired: boolean;
  numberLabel: string | null;
  checkboxRequired: boolean;
  checkboxLabel: string | null;
}

interface Task extends Requirements {
  id: string;
  storeId: string;
  title: string;
  section: Section;
  status: Status;
  dueAt: string | null;
  assignedTo: string | null;
  completedAt: string | null;
  completedById: string | null;
  completedByTier: 'manager' | 'store_ops' | null;
}

interface TaskTemplate extends Requirements {
  id: string;
  storeId: string | null;
  title: string;
  section: Section;
  cadence: Cadence;
  dueTimeLocal: string | null;
  isActive: boolean;
}

interface ListResponse {
  tasks: Task[];
}

const SECTIONS: { key: Section; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'daily', label: 'Daily' },
  { key: 'closing', label: 'Closing' },
  { key: 'weekly', label: 'Weekly' },
];

const inputClass = 'mt-1 w-full border border-border rounded-md px-2 py-1';

function requirementChips(t: Requirements): string[] {
  const chips: string[] = [];
  if (t.photoRequired) chips.push('photo required');
  if (t.numberRequired) chips.push(t.numberLabel ? `enter: ${t.numberLabel}` : 'number required');
  if (t.checkboxRequired)
    chips.push(t.checkboxLabel ? `confirm: ${t.checkboxLabel}` : 'confirmation required');
  if (t.signatureRequired) chips.push('signature required');
  return chips;
}

/**
 * Task list grouped by section. Tier-2 baristas always see their home store;
 * Tier-1 managers see a store picker when their scope spans multiple stores,
 * can add ad-hoc tasks, and can manage the recurring templates that the daily
 * generator materializes per store.
 */
export function Tasks() {
  const { tier, scopedStores } = useUserContext();
  const { storeLabel, inScope } = useViewScope();
  // Tasks are inherently single-store; the picker follows the header view
  // scope, so narrowing to a region/store narrows the choices here too.
  const pickableStores = scopedStores.filter(inScope);
  const [storeId, setStoreId] = useState<string>(pickableStores[0] ?? '');
  const [view, setView] = useState<'tasks' | 'recurring'>('tasks');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeCompletion, setActiveCompletion] = useState<Task | null>(null);
  const [adHocOpen, setAdHocOpen] = useState(false);

  useEffect(() => {
    // Keep the selected store inside the (possibly narrowed) pickable set.
    if ((!storeId || !pickableStores.includes(storeId)) && pickableStores[0]) {
      setStoreId(pickableStores[0]);
    }
  }, [pickableStores, storeId]);

  const reload = useCallback(() => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    const p = new URLSearchParams();
    p.set('storeId', storeId);
    p.set('status', 'open');
    apiFetch<ListResponse>(`/tasks?${p.toString()}`)
      .then((r) => setTasks(r.tasks))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [storeId]);

  useEffect(() => {
    if (storeId) reload();
  }, [storeId, reload]);

  const grouped = useMemo(() => {
    const out: Record<Section, Task[]> = { open: [], daily: [], closing: [], weekly: [] };
    for (const t of tasks) {
      out[t.section]?.push(t);
    }
    return out;
  }, [tasks]);

  const isManager = tier === 'manager';

  return (
    <div className="min-h-screen p-6 max-w-3xl mx-auto">
      <header className="flex items-center justify-between gap-3 border-b border-border pb-4 mb-4">
        <h1 className="text-2xl font-semibold">Tasks</h1>
        <div className="flex items-center gap-2">
          {pickableStores.length > 1 && isManager ? (
            <select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              className="border border-border rounded-md px-2 py-1 text-sm"
            >
              {pickableStores.map((id) => (
                <option key={id} value={id}>
                  {storeLabel(id)}
                </option>
              ))}
            </select>
          ) : null}
          {isManager ? (
            <>
              <button
                type="button"
                onClick={() => setView(view === 'recurring' ? 'tasks' : 'recurring')}
                className="px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-muted"
              >
                {view === 'recurring' ? 'Back to tasks' : 'Recurring'}
              </button>
              {view === 'tasks' ? (
                <button
                  type="button"
                  onClick={() => setAdHocOpen(true)}
                  disabled={!storeId}
                  className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
                >
                  New task
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </header>

      {!storeId ? (
        <p className="text-sm text-muted-foreground">
          No store in your scope yet — contact your admin.
        </p>
      ) : null}

      {view === 'recurring' && isManager && storeId ? (
        <RecurringTemplates storeId={storeId} storeLabel={storeLabel} />
      ) : (
        <>
          {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
          {loading && tasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : null}

          {SECTIONS.map((sec) => {
            const list = grouped[sec.key];
            if (!list || list.length === 0) return null;
            return (
              <section key={sec.key} className="mb-6">
                <h2 className="text-sm font-medium uppercase text-muted-foreground mb-2">
                  {sec.label}
                </h2>
                <ul className="space-y-2">
                  {list.map((t) => (
                    <li
                      key={t.id}
                      className="flex items-start gap-3 border border-border rounded-md p-3 bg-card"
                    >
                      <button
                        type="button"
                        onClick={() => setActiveCompletion(t)}
                        className="mt-0.5 h-5 w-5 rounded border border-border hover:bg-muted shrink-0"
                        aria-label={`Complete ${t.title}`}
                      />
                      <div className="flex-1">
                        <div className="text-sm">{t.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {t.dueAt ? `due ${new Date(t.dueAt).toLocaleTimeString()} · ` : ''}
                          {t.assignedTo ? `→ ${t.assignedTo}` : 'unassigned'}
                        </div>
                        {requirementChips(t).length > 0 ? (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {requirementChips(t).map((c) => (
                              <span
                                key={c}
                                className="text-[11px] rounded-full bg-secondary text-secondary-foreground px-2 py-0.5"
                              >
                                {c}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}

          {tasks.length === 0 && !loading && storeId ? (
            <p className="text-sm text-muted-foreground">All clear. No open tasks.</p>
          ) : null}
        </>
      )}

      {activeCompletion ? (
        <CompleteDialog
          task={activeCompletion}
          onClose={() => setActiveCompletion(null)}
          onDone={() => {
            setActiveCompletion(null);
            reload();
          }}
        />
      ) : null}
      {adHocOpen && storeId ? (
        <AdHocTaskDialog
          storeId={storeId}
          storeName={storeLabel(storeId)}
          onClose={() => setAdHocOpen(false)}
          onCreated={() => {
            setAdHocOpen(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

// ---- Requirement editor (shared by ad-hoc + template forms) -------------------

interface RequirementDraft {
  photoRequired: boolean;
  numberRequired: boolean;
  numberLabel: string;
  checkboxRequired: boolean;
  checkboxLabel: string;
}

const emptyRequirements: RequirementDraft = {
  photoRequired: false,
  numberRequired: false,
  numberLabel: '',
  checkboxRequired: false,
  checkboxLabel: '',
};

function RequirementFields({
  value,
  onChange,
}: {
  value: RequirementDraft;
  onChange: (next: RequirementDraft) => void;
}) {
  return (
    <fieldset className="space-y-2 border border-border rounded-md p-3">
      <legend className="text-sm font-medium px-1">Mandatory on completion</legend>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.photoRequired}
          onChange={(e) => onChange({ ...value, photoRequired: e.target.checked })}
        />
        Photo proof
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.numberRequired}
          onChange={(e) => onChange({ ...value, numberRequired: e.target.checked })}
        />
        Numeric reading
      </label>
      {value.numberRequired ? (
        <input
          value={value.numberLabel}
          onChange={(e) => onChange({ ...value, numberLabel: e.target.value })}
          placeholder="what to record, e.g. Fridge temp (°F)"
          maxLength={64}
          className="w-full border border-border rounded-md px-2 py-1 text-sm"
        />
      ) : null}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.checkboxRequired}
          onChange={(e) => onChange({ ...value, checkboxRequired: e.target.checked })}
        />
        Checkbox confirmation
      </label>
      {value.checkboxRequired ? (
        <input
          value={value.checkboxLabel}
          onChange={(e) => onChange({ ...value, checkboxLabel: e.target.value })}
          placeholder="what to confirm, e.g. Sanitizer levels checked"
          maxLength={128}
          className="w-full border border-border rounded-md px-2 py-1 text-sm"
        />
      ) : null}
    </fieldset>
  );
}

function requirementPayload(r: RequirementDraft) {
  return {
    photoRequired: r.photoRequired,
    numberRequired: r.numberRequired,
    numberLabel: r.numberRequired && r.numberLabel.trim() ? r.numberLabel.trim() : null,
    checkboxRequired: r.checkboxRequired,
    checkboxLabel: r.checkboxRequired && r.checkboxLabel.trim() ? r.checkboxLabel.trim() : null,
  };
}

// ---- Ad-hoc task creation ------------------------------------------------------

function AdHocTaskDialog({
  storeId,
  storeName,
  onClose,
  onCreated,
}: {
  storeId: string;
  storeName: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [section, setSection] = useState<Section>('open');
  const [dueTime, setDueTime] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [reqs, setReqs] = useState<RequirementDraft>(emptyRequirements);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!title.trim()) {
      setErr('title required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      let dueAt: string | undefined;
      if (dueTime) {
        const [h, m] = dueTime.split(':').map(Number);
        const d = new Date();
        d.setHours(h ?? 0, m ?? 0, 0, 0);
        dueAt = d.toISOString();
      }
      await apiFetch('/tasks', {
        method: 'POST',
        body: JSON.stringify({
          storeId,
          title: title.trim(),
          section,
          dueAt,
          assignedTo: assignedTo.trim() || undefined,
          ...requirementPayload(reqs),
        }),
      });
      onCreated();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'create failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-md space-y-3 my-8">
        <h3 className="text-lg font-semibold">New task</h3>
        <p className="text-xs text-muted-foreground">One-off task for {storeName}</p>
        <label className="block text-sm">
          Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            Section
            <select
              value={section}
              onChange={(e) => setSection(e.target.value as Section)}
              className={inputClass}
            >
              {SECTIONS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            Due today at (optional)
            <input
              type="time"
              value={dueTime}
              onChange={(e) => setDueTime(e.target.value)}
              className={inputClass}
            />
          </label>
        </div>
        <label className="block text-sm">
          Assign to (optional)
          <input
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            placeholder="username"
            maxLength={64}
            className={inputClass}
          />
        </label>
        <RequirementFields value={reqs} onChange={setReqs} />
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
          >
            {busy ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Recurring templates management -------------------------------------------

function RecurringTemplates({
  storeId,
  storeLabel,
}: {
  storeId: string;
  storeLabel: (id: string) => string;
}) {
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<TaskTemplate | 'new' | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch<{ templates: TaskTemplate[] }>('/task-templates?includeInactive=1')
      .then((r) => setTemplates(r.templates))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const visible = templates.filter((t) => t.storeId === storeId || t.storeId === null);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">
          Recurring tasks for <span className="font-medium">{storeLabel(storeId)}</span> (org-wide
          templates included). The generator creates these daily/weekly.
        </p>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium shrink-0"
        >
          Add recurring
        </button>
      </div>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
      {loading && templates.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : null}

      <ul className="space-y-2">
        {visible.map((t) => (
          <li
            key={t.id}
            className={`border border-border rounded-md p-3 bg-card ${t.isActive ? '' : 'opacity-60'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium">
                {t.title}
                {!t.isActive ? (
                  <span className="ml-2 text-[11px] uppercase text-muted-foreground">inactive</span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setEditing(t)}
                className="text-sm underline underline-offset-4 text-muted-foreground hover:text-foreground"
              >
                Edit
              </button>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {t.storeId === null ? 'org-wide' : storeLabel(t.storeId)} · {t.cadence} · section{' '}
              {t.section}
              {t.dueTimeLocal ? ` · due ${t.dueTimeLocal}` : ''}
            </div>
            {requirementChips(t).length > 0 ? (
              <div className="flex flex-wrap gap-1 mt-1">
                {requirementChips(t).map((c) => (
                  <span
                    key={c}
                    className="text-[11px] rounded-full bg-secondary text-secondary-foreground px-2 py-0.5"
                  >
                    {c}
                  </span>
                ))}
              </div>
            ) : null}
          </li>
        ))}
        {!loading && visible.length === 0 ? (
          <li className="py-6 text-center text-sm text-muted-foreground">
            No recurring tasks for this store yet.
          </li>
        ) : null}
      </ul>

      {editing ? (
        <TemplateDialog
          template={editing === 'new' ? null : editing}
          storeId={storeId}
          storeName={storeLabel(storeId)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function TemplateDialog({
  template,
  storeId,
  storeName,
  onClose,
  onSaved,
}: {
  template: TaskTemplate | null;
  storeId: string;
  storeName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = template === null;
  const [title, setTitle] = useState(template?.title ?? '');
  const [section, setSection] = useState<Section>(template?.section ?? 'daily');
  const [cadence, setCadence] = useState<Cadence>(template?.cadence ?? 'daily');
  const [dueTime, setDueTime] = useState(template?.dueTimeLocal ?? '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [reqs, setReqs] = useState<RequirementDraft>(
    template
      ? {
          photoRequired: template.photoRequired,
          numberRequired: template.numberRequired,
          numberLabel: template.numberLabel ?? '',
          checkboxRequired: template.checkboxRequired,
          checkboxLabel: template.checkboxLabel ?? '',
        }
      : emptyRequirements,
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const orgWide = template !== null && template.storeId === null;

  async function submit() {
    if (!title.trim()) {
      setErr('title required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const shared = {
        title: title.trim(),
        section,
        cadence,
        dueTimeLocal: dueTime || null,
        ...requirementPayload(reqs),
      };
      if (isNew) {
        await apiFetch('/task-templates', {
          method: 'POST',
          body: JSON.stringify({ ...shared, storeId, dueTimeLocal: dueTime || undefined }),
        });
      } else {
        await apiFetch(`/task-templates/${template.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ ...shared, isActive }),
        });
      }
      onSaved();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-md space-y-3 my-8">
        <h3 className="text-lg font-semibold">
          {isNew ? 'Add recurring task' : 'Edit recurring task'}
        </h3>
        <p className="text-xs text-muted-foreground">
          {isNew ? `For ${storeName}` : orgWide ? 'Org-wide template' : `For ${storeName}`}
        </p>
        <label className="block text-sm">
          Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
        </label>
        <div className="grid grid-cols-3 gap-2">
          <label className="block text-sm">
            Section
            <select
              value={section}
              onChange={(e) => setSection(e.target.value as Section)}
              className={inputClass}
            >
              {SECTIONS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            Cadence
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value as Cadence)}
              className={inputClass}
            >
              <option value="daily">daily</option>
              <option value="weekly">weekly</option>
              <option value="once">once</option>
            </select>
          </label>
          <label className="block text-sm">
            Due at
            <input
              type="time"
              value={dueTime}
              onChange={(e) => setDueTime(e.target.value)}
              className={inputClass}
            />
          </label>
        </div>
        <RequirementFields value={reqs} onChange={setReqs} />
        {!isNew ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active (inactive templates stop generating tasks)
          </label>
        ) : null}
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
          >
            {busy ? 'Saving…' : isNew ? 'Add' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Completion ---------------------------------------------------------------

function CompleteDialog({
  task,
  onClose,
  onDone,
}: {
  task: Task;
  onClose: () => void;
  onDone: () => void;
}) {
  const [notes, setNotes] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [numberValue, setNumberValue] = useState('');
  const [checkboxConfirmed, setCheckboxConfirmed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const needsPhoto = task.photoRequired;
  const needsNumber = task.numberRequired;
  const needsCheckbox = task.checkboxRequired;

  const missingRequirement =
    (needsPhoto && !photoUrl) ||
    (needsNumber && numberValue.trim() === '') ||
    (needsNumber && Number.isNaN(Number(numberValue))) ||
    (needsCheckbox && !checkboxConfirmed);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setErr(null);
    try {
      const url = await uploadFile(f, 'task-proof', task.storeId);
      setPhotoUrl(url);
    } catch (e2: unknown) {
      setErr(e2 instanceof Error ? e2.message : 'upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function complete(skip = false) {
    setSubmitting(true);
    setErr(null);
    try {
      const body: {
        notes?: string;
        photoBlobUrls?: string[];
        numberValue?: number;
        checkboxConfirmed?: boolean;
        skip?: boolean;
      } = {};
      if (notes) body.notes = notes;
      if (photoUrl) body.photoBlobUrls = [photoUrl];
      if (!skip && needsNumber && numberValue.trim() !== '') {
        body.numberValue = Number(numberValue);
      }
      if (!skip && needsCheckbox) body.checkboxConfirmed = checkboxConfirmed;
      if (skip) body.skip = true;
      await apiFetch(`/tasks/${task.id}/complete`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'complete failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md space-y-3">
        <h3 className="text-lg font-semibold">{task.title}</h3>
        <p className="text-xs text-muted-foreground">
          {task.section} ·{' '}
          {task.dueAt ? `due ${new Date(task.dueAt).toLocaleTimeString()}` : 'no due time'}
        </p>
        <label className="block text-sm">
          Notes (optional)
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={`${inputClass} min-h-[60px]`}
            placeholder="anything to flag for the next shift…"
          />
        </label>
        {needsNumber ? (
          <label className="block text-sm">
            {task.numberLabel ?? 'Reading'} (required)
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={numberValue}
              onChange={(e) => setNumberValue(e.target.value)}
              className={inputClass}
              placeholder="enter a number"
            />
          </label>
        ) : null}
        {needsCheckbox ? (
          <label className="flex items-start gap-2 text-sm border border-border rounded-md p-3">
            <input
              type="checkbox"
              checked={checkboxConfirmed}
              onChange={(e) => setCheckboxConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              {task.checkboxLabel ?? 'I confirm this task was done as required'}{' '}
              <span className="text-muted-foreground">(required)</span>
            </span>
          </label>
        ) : null}
        {needsPhoto ? (
          <div className="space-y-1">
            <label className="block text-sm">Photo (required)</label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => void onPickFile(e)}
              disabled={uploading || submitting}
              className="text-xs"
            />
            {uploading ? (
              <p className="text-xs text-muted-foreground">Uploading…</p>
            ) : photoUrl ? (
              <p className="text-xs text-emerald-700">
                ✓ uploaded · <code className="break-all">{photoUrl}</code>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Snap or attach an image of the completed task.
              </p>
            )}
          </div>
        ) : null}
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => void complete(true)}
            disabled={submitting}
            className="px-3 py-1 text-sm text-amber-700 underline underline-offset-4"
          >
            Skip
          </button>
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void complete(false)}
            disabled={submitting || missingRequirement}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
          >
            {submitting ? 'Saving…' : 'Mark complete'}
          </button>
        </div>
      </div>
    </div>
  );
}
